/**
 * „Popraw wystawiony protokół w CEEB" (decyzja właściciela 2026-10-09) — czyste reguły dla PipelineView:
 * kiedy pokazać akcję i jak odczytać pola trybu `edit-issued` ze status-file'u wysyłki.
 * To tylko widoczność przycisku — serwer i tak sprawdza wszystko jeszcze raz
 * (`GET /buildings/:id/ceeb-edit-issued-check`) i bez udanej kopii protokołu bot niczego nie zmienia.
 * Rolę superadmina i przełącznik `CEEB_EDIT_ISSUED` (GET /ceeb/flags) sprawdza wołający.
 */
import type { CeebEditIssuedAlarmChange, CeebPushStatusResponse, PipelineBuilding } from '../services/api';

type ProtocolRow = Pick<PipelineBuilding, 'ceebStatus' | 'ceebAcceptedAt' | 'apt_pending_ceeb' | 'ceebCaseNumber'>;

/** Numer sprawy bez białych znaków — tak samo porównuje serwer (`normCase` w backend/src/utils/ceebEditIssued.js). */
export function normCaseNumber(v?: string | null): string {
  return String(v ?? '').replace(/\s+/g, '');
}

/** Rok sprawy z numeru „NNNNNN/RRRR"; null, gdy numeru brak albo ma inny format. */
export function caseYear(caseNumber?: string | null): number | null {
  const m = /^\d+\/(\d{4})$/.exec(normCaseNumber(caseNumber));
  return m ? Number(m[1]) : null;
}

/** Bieżący rok w strefie Europe/Warsaw — „bieżąca sprawa" liczy się po polskim kalendarzu, nie po strefie przeglądarki. */
export function warsawYear(now: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Warsaw', year: 'numeric' }).format(now));
}

/**
 * Przycisk w wierszu: protokół „Wprowadzony", niezaakceptowany, lokale z terenu czekają na CEEB,
 * sprawa z bieżącego roku. Cudze/zeszłoroczne protokoły — nigdy.
 */
export function canEditIssued(b: ProtocolRow, now: Date = new Date()): boolean {
  return b.ceebStatus === 'Wprowadzony' && !b.ceebAcceptedAt && b.apt_pending_ceeb > 0
    && caseYear(b.ceebCaseNumber) === warsawYear(now);
}

/** Protokół zaakceptowany, a lokale z terenu czekają — poprawka tylko ręcznie w CEEB (wyłączony przycisk z podpowiedzią). */
export function acceptedNeedsManualFix(b: ProtocolRow): boolean {
  return b.ceebStatus === 'Zaakceptowany' && b.apt_pending_ceeb > 0;
}

export const ACCEPTED_MANUAL_FIX_HINT =
  'Protokół zaakceptowany — poprawka tylko ręcznie w CEEB (anulowanie akceptacji jeszcze niedostępne w aplikacji)';

/** Odmowy zwykłej wysyłki/finalizacji, po których ma sens poprawka wystawionego protokołu. */
const ISSUED_PUSH_CODES = new Set(['PROTOCOL_ISSUED', 'TARGET_STATUS_REJECTED']);
/** Lokal, którego wiersz w CEEB jest już wystawiony (reguła 3a `selectSyncedApartments`). */
const COPY_BLOCKED_CODE = 'ceeb_copy_blocked';

type PushOutcome = Pick<CeebPushStatusResponse, 'mode' | 'code' | 'next' | 'copyBlocked' | 'ceebStatus'> & {
  status: string;
  perApartment?: Array<{ code?: string }>;
};

/**
 * Czy obok wyniku „Wyślij do CEEB" / „Finalizuj w CEEB" zaproponować poprawkę:
 *  - serwer sam podpowiedział `next: 'edit-issued'` (flaga włączona, protokół „Wprowadzony");
 *  - odmowa PROTOCOL_ISSUED / TARGET_STATUS_REJECTED przy protokole „Wprowadzony" (status z odmowy bota,
 *    a gdy go nie ma — z listy) i bez akceptacji;
 *  - zakończony zapis z lokalami, których wiersz w CEEB jest już wystawiony (`copyBlocked` / `ceeb_copy_blocked`) —
 *    tylko dla budynku spełniającego regułę przycisku w wierszu.
 * Wynik samej poprawki nigdy nie proponuje kolejnej poprawki.
 */
export function offerEditIssuedAfterPush(b: ProtocolRow, push: PushOutcome | undefined, now: Date = new Date()): boolean {
  if (!push || push.mode === 'edit-issued') return false;
  if (push.status === 'error') {
    if (push.next === 'edit-issued') return true;
    if (!push.code || !ISSUED_PUSH_CODES.has(push.code)) return false;
    return (push.ceebStatus || b.ceebStatus) === 'Wprowadzony' && !b.ceebAcceptedAt;
  }
  if (push.status === 'done') {
    const copies = !!push.copyBlocked || (push.perApartment || []).some((a) => a.code === COPY_BLOCKED_CODE);
    return copies && canEditIssued(b, now);
  }
  return false;
}

type EditIssuedFields = Pick<CeebPushStatusResponse,
  'mode' | 'dryRun' | 'step' | 'code' | 'next' | 'ceebStatus' | 'alarm' | 'editIssued' | 'reportUrl' | 'copyBlocked'
  | 'snapshotBeforeId' | 'snapshotAfterId'>;

/**
 * Pola trybu poprawki (i odmowy zwykłej wysyłki) ze status-file'u do stanu wiersza. `mode`/`dryRun` z poprzedniego
 * stanu (ustawione przy starcie), gdy plik ich nie niesie — „próba bez zapisu" nie może się pokazać jako „poprawiono".
 */
export function editIssuedFields(st: CeebPushStatusResponse, prev?: Pick<EditIssuedFields, 'mode' | 'dryRun'>): EditIssuedFields {
  return {
    mode: st.mode ?? prev?.mode,
    dryRun: st.dryRun ?? prev?.dryRun,
    step: st.step,
    code: st.code,
    next: st.next,
    ceebStatus: st.ceebStatus,
    alarm: st.alarm,
    editIssued: st.editIssued,
    reportUrl: st.reportUrl,
    copyBlocked: st.copyBlocked,
    snapshotBeforeId: st.snapshotBeforeId,
    snapshotAfterId: st.snapshotAfterId,
  };
}

function alarmValue(v: unknown): string {
  if (v == null || v === '') return '(puste)';
  if (typeof v === 'string') return `„${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

/** Jedna nieoczekiwana zmiana alarmu w jednym zdaniu: „ścieżka: przed → po (powód)". */
export function alarmChangeText(u: CeebEditIssuedAlarmChange): string {
  return `${u.path}: ${alarmValue(u.before)} → ${alarmValue(u.after)}${u.why ? ` (${u.why})` : ''}`;
}

/** Etykiety faz kopii protokołu (`ceeb_protocol_snapshots.phase`, migr. 063); nieznana faza — dosłownie. */
const SNAPSHOT_PHASE: Record<string, string> = {
  before: 'przed zmianą',
  after: 'po zmianie',
  'pre-restore': 'przed odtworzeniem',
  'after-restore': 'po odtworzeniu',
  probe: 'sonda',
};

export function snapshotPhaseLabel(phase: string): string {
  return SNAPSHOT_PHASE[phase] || phase;
}
